import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react';
import AddItemModal from './ListBuilder/AddItemModal';
import AsyncSearchSelect from '../../TableSearch/AsyncSearchSelect';
import AsyncMultiSearchSelect from '../../TableSearch/AsyncMultiSearchSelect';
import LgmButton from '../../LgmButton';
import {
  GRAY_200,
  GRAY_300,
  GRAY_400,
  GRAY_50,
  GRAY_500,
  GRAY_700,
} from 'theme/grayScale';
import { COLOR_DANGER_TEXT } from 'theme/colors';
import { MANUFACTURER_PANEL_COLORS } from 'theme/sourcePanelColors';

/**
 * Stable reference for an empty async-multi value. A fresh `[]` literal on
 * every render would give AsyncMultiSearchSelect's `value` prop a new
 * identity each time even when genuinely empty, re-triggering its sync
 * effect on every unrelated re-render and resetting any in-progress
 * selection before the parent's onChange round-trip lands.
 */
const EMPTY_ASYNC_MULTI_VALUE: string[] = [];

export interface StaticListBuilderColumn {
  kind: 'static';
  key: string;
  label: string;
  /** Required when asyncFetch is absent. Dropdown options for the column. */
  options: Array<{ value: string; label: string }>;
}

export interface AsyncListBuilderColumn {
  kind: 'async';
  key: string;
  label: string;
  /** API path WITHOUT the /api prefix, e.g. '/grades'. When set, asyncValueField, asyncLabelField, and asyncSearchField are all required. */
  asyncFetch: string;
  /** Required when asyncFetch is set. Field on the API response item to use as the search parameter. */
  asyncSearchField: string;
  /** Required when asyncFetch is set. Field on the API response item to use as the value (ID). */
  asyncValueField: string;
  /** Required when asyncFetch is set. Field on the API response item to use as the display label. */
  asyncLabelField: string;
}

/** Same shape as AsyncListBuilderColumn, but the column can hold 2+ selected values per row. */
export interface AsyncMultiListBuilderColumn {
  kind: 'async-multi';
  key: string;
  label: string;
  asyncFetch: string;
  asyncSearchField: string;
  asyncValueField: string;
  asyncLabelField: string;
}

export interface TextListBuilderColumn {
  kind: 'text';
  key: string;
  label: string;
  /** Maximum allowed length for the text input (default 450). */
  maxLength?: number;
}

export type ListBuilderStatusColumn =
  | StaticListBuilderColumn
  | AsyncListBuilderColumn
  | AsyncMultiListBuilderColumn
  | TextListBuilderColumn;

export interface ListBuilderItem {
  id: number;
  label: string;
  status?: string | null;
  fields?: Record<string, string | string[] | null>;
}

export interface ListBuilderConfig {
  idKey: string;
  labelKey: string;
  statusKey?: string;
  statusOptions?: { value: string; label: string }[];
  statusColumns?: ListBuilderStatusColumn[];
  addButtonText?: string;
  addModalTitle?: string;
  noItemsText?: string;
  /** Panel-header badge text. Defaults to 'MFR' (the Product modal's
   * original — and only — use case: a list of manufacturers). Issue #653,
   * Task 10 Sub-task 10.4 reuses this component for a list of PRODUCTS on
   * the Manufacturer modal, where a hardcoded 'MFR' badge would misreport
   * the row type — pass an explicit value for any non-manufacturer list. */
  badgeText?: string;
}

export interface ListBuilderProps {
  id: string;
  label: string;
  value: ListBuilderItem[];
  onChange?: (value: ListBuilderItem[]) => void;
  availableItems: ListBuilderItem[];
  config: ListBuilderConfig;
  disabled?: boolean;
  required?: boolean;
  styles: Record<string, React.CSSProperties>;
  /** When true, the panel header becomes a collapse/expand toggle for the
   * table + add button below it (issue #653, Task 16 QA follow-up: was
   * previously a separate CollapsibleSection the caller wrapped this
   * component in — moved in-house so the item count and collapse toggle
   * live next to the badge/label they describe, instead of a second
   * "Products"-ish header stacked above this one). Off by default — the
   * product modal's own manufacturer-list usage never collapses. */
  collapsible?: boolean;
  /** Only meaningful when `collapsible` is true. */
  defaultCollapsed?: boolean;
}

const ListBuilder: React.FC<ListBuilderProps> = ({
  id: _id,
  label,
  value,
  onChange,
  availableItems,
  config,
  disabled = false,
  required = false,
  styles,
  collapsible = false,
  defaultCollapsed = false,
}) => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(
    collapsible && defaultCollapsed
  );
  const isExpanded = !collapsible || !isCollapsed;

  const handleStatusChange = (itemId: number, newStatus: string | null) => {
    if (!onChange) return;
    const updatedItems = value.map(item =>
      item.id === itemId ? { ...item, status: newStatus } : item
    );
    onChange(updatedItems);
  };

  const handleFieldChange = (
    itemId: number,
    fieldKey: string,
    fieldValue: string | string[] | null
  ) => {
    if (!onChange) return;
    const updatedItems = value.map(item =>
      item.id === itemId
        ? { ...item, fields: { ...item.fields, [fieldKey]: fieldValue } }
        : item
    );
    onChange(updatedItems);
  };

  const handleRemoveItem = (itemId: number) => {
    if (!onChange) return;
    const updatedItems = value.filter(item => item.id !== itemId);
    onChange(updatedItems);
  };

  const handleAddItems = (selectedIds: number[]) => {
    if (!onChange) return;
    const newItems = selectedIds
      .map(selectedId => availableItems.find(item => item.id === selectedId))
      .filter((item): item is ListBuilderItem => item !== undefined)
      .map(item => ({
        ...item,
        status: null, // Default status is null
      }));
    onChange([...value, ...newItems]);
    setIsAddModalOpen(false);
  };

  // Filter out items that are already in the list
  const filteredAvailableItems = availableItems.filter(
    item => !value.some(v => v.id === item.id)
  );

  const statusOptions = config.statusOptions || [];
  const hasStatusColumn = !!config.statusKey;
  const extraColumns = config.statusColumns || [];

  return (
    <div style={styles.floatingFieldContainer}>
      <div
        style={{
          border: `1px solid ${MANUFACTURER_PANEL_COLORS.border}`,
          borderRadius: '6px',
          overflow: 'hidden',
          backgroundColor: MANUFACTURER_PANEL_COLORS.background,
          ...(disabled ? listBuilderStyles.containerDisabled : {}),
        }}>
        {/* Panel header — badge/label/count/chevron are the collapse toggle
            when `collapsible`; the Add button is a separate sibling so it's
            always visible (mirrors OrangeBookPanel.tsx's own header, whose
            "+ Link to Orange Book" button sits outside the panel's inner
            CollapsibleSection) and never fights the toggle for clicks. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '8px',
            padding: '8px 12px',
            backgroundColor: MANUFACTURER_PANEL_COLORS.headerBackground,
            borderBottom: isExpanded
              ? `1px solid ${MANUFACTURER_PANEL_COLORS.border}`
              : 'none',
          }}>
          <div
            role={collapsible ? 'button' : undefined}
            tabIndex={collapsible ? 0 : undefined}
            aria-expanded={collapsible ? isExpanded : undefined}
            aria-label={collapsible ? `${label} (${value.length})` : undefined}
            onClick={
              collapsible ? () => setIsCollapsed(prev => !prev) : undefined
            }
            onKeyDown={
              collapsible
                ? e => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setIsCollapsed(prev => !prev);
                    }
                  }
                : undefined
            }
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '8px',
              flex: 1,
              minWidth: 0,
              cursor: collapsible ? 'pointer' : 'default',
            }}>
            <span
              style={{
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                color: MANUFACTURER_PANEL_COLORS.badgeText,
                backgroundColor: MANUFACTURER_PANEL_COLORS.badgeBackground,
                padding: '2px 6px',
                borderRadius: '4px',
              }}>
              {config.badgeText ?? 'MFR'}
              {required && <span style={{ color: 'red' }}>*</span>}
            </span>
            {collapsible && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span
                  style={{
                    fontSize: '12px',
                    fontWeight: 400,
                    color: MANUFACTURER_PANEL_COLORS.badgeText,
                  }}>
                  ({value.length})
                </span>
                {isExpanded ? (
                  <ChevronDown
                    size={16}
                    style={{ color: MANUFACTURER_PANEL_COLORS.headerText }}
                  />
                ) : (
                  <ChevronRight
                    size={16}
                    style={{ color: MANUFACTURER_PANEL_COLORS.headerText }}
                  />
                )}
              </div>
            )}
          </div>
          <LgmButton
            variant="outlined"
            onClick={() => setIsAddModalOpen(true)}
            disabled={disabled || filteredAvailableItems.length === 0}
            style={{ fontSize: '12px', padding: '4px 10px' }}>
            + {config.addButtonText || 'Add Item'}
          </LgmButton>
        </div>

        {isExpanded && (
          <div style={{ padding: '12px' }}>
            {/* Table */}
            {value.length > 0 ? (
              <div style={listBuilderStyles.tableContainer}>
                <table style={listBuilderStyles.table}>
                  <thead>
                    <tr style={listBuilderStyles.tableHeaderRow}>
                      <th style={listBuilderStyles.tableHeaderCell}>
                        {config.labelKey}
                      </th>
                      {hasStatusColumn && (
                        <th style={listBuilderStyles.tableHeaderCell}>
                          {config.statusKey}
                        </th>
                      )}
                      {extraColumns.map(col => (
                        <th
                          key={col.key}
                          style={listBuilderStyles.tableHeaderCell}>
                          {col.label}
                        </th>
                      ))}
                      <th style={listBuilderStyles.tableHeaderCellActions}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {value.map(item => {
                      return (
                        <tr key={item.id} style={listBuilderStyles.tableRow}>
                          <td
                            style={{
                              ...listBuilderStyles.tableCell,
                              ...(disabled
                                ? listBuilderStyles.tableCellDisabled
                                : {}),
                            }}>
                            {item.label}
                          </td>
                          {hasStatusColumn && (
                            <td style={listBuilderStyles.tableCellStatus}>
                              <select
                                value={item.status || ''}
                                onChange={e => {
                                  handleStatusChange(
                                    item.id,
                                    e.target.value || null
                                  );
                                }}
                                disabled={disabled}
                                style={{
                                  ...listBuilderStyles.statusSelect,
                                  ...(disabled
                                    ? listBuilderStyles.statusSelectDisabled
                                    : {}),
                                }}>
                                <option value="">None</option>
                                {statusOptions.map(opt => (
                                  <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                  </option>
                                ))}
                              </select>
                            </td>
                          )}
                          {extraColumns.map(col => {
                            const fieldVal = item.fields?.[col.key] ?? null;
                            if (disabled) {
                              const scalarFieldVal =
                                typeof fieldVal === 'string' ? fieldVal : '';
                              const labelVal =
                                col.kind === 'async' ||
                                col.kind === 'async-multi'
                                  ? (item.fields?.[col.asyncLabelField] ??
                                    fieldVal ??
                                    '')
                                  : col.kind === 'text'
                                    ? (fieldVal ?? '\u2014')
                                    : (col.options.find(
                                        o => o.value === scalarFieldVal
                                      )?.label ??
                                      scalarFieldVal ??
                                      '');
                              const displayLabel = Array.isArray(labelVal)
                                ? labelVal.join(', ') || '\u2014'
                                : labelVal;
                              return (
                                <td
                                  key={col.key}
                                  style={{
                                    ...listBuilderStyles.tableCell,
                                    ...listBuilderStyles.tableCellDisabled,
                                  }}>
                                  {displayLabel}
                                </td>
                              );
                            }
                            if (col.kind === 'async') {
                              const asyncVal =
                                typeof fieldVal === 'string' ? fieldVal : '';
                              const asyncLabel =
                                item.fields?.[col.asyncLabelField];
                              return (
                                <td
                                  key={col.key}
                                  style={listBuilderStyles.tableCellStatus}>
                                  <AsyncSearchSelect
                                    value={asyncVal}
                                    onChange={v =>
                                      handleFieldChange(item.id, col.key, v)
                                    }
                                    apiEndpoint={col.asyncFetch}
                                    searchField={col.asyncSearchField}
                                    valueField={col.asyncValueField}
                                    labelField={col.asyncLabelField}
                                    placeholder={`Select ${col.label}`}
                                    initialLabel={
                                      typeof asyncLabel === 'string'
                                        ? asyncLabel
                                        : undefined
                                    }
                                  />
                                </td>
                              );
                            }
                            if (col.kind === 'async-multi') {
                              const multiVal = Array.isArray(fieldVal)
                                ? fieldVal
                                : EMPTY_ASYNC_MULTI_VALUE;
                              const multiLabels =
                                item.fields?.[col.asyncLabelField];
                              return (
                                <td
                                  key={col.key}
                                  style={listBuilderStyles.tableCellStatus}>
                                  <AsyncMultiSearchSelect
                                    value={multiVal}
                                    onChange={v =>
                                      handleFieldChange(item.id, col.key, v)
                                    }
                                    apiEndpoint={col.asyncFetch}
                                    searchField={col.asyncSearchField}
                                    valueField={col.asyncValueField}
                                    labelField={col.asyncLabelField}
                                    placeholder={`Select ${col.label}`}
                                    initialLabels={
                                      Array.isArray(multiLabels)
                                        ? multiLabels
                                        : undefined
                                    }
                                  />
                                </td>
                              );
                            }
                            if (col.kind === 'text') {
                              const textVal =
                                typeof fieldVal === 'string' ? fieldVal : '';
                              return (
                                <td
                                  key={col.key}
                                  style={listBuilderStyles.tableCellStatus}>
                                  <input
                                    type="text"
                                    maxLength={col.maxLength ?? 450}
                                    value={textVal}
                                    onChange={e =>
                                      handleFieldChange(
                                        item.id,
                                        col.key,
                                        e.target.value || null
                                      )
                                    }
                                    placeholder={'—'}
                                    style={listBuilderStyles.textInput}
                                  />
                                </td>
                              );
                            }
                            const staticVal =
                              typeof fieldVal === 'string' ? fieldVal : '';
                            return (
                              <td
                                key={col.key}
                                style={listBuilderStyles.tableCellStatus}>
                                <select
                                  value={staticVal}
                                  onChange={e =>
                                    handleFieldChange(
                                      item.id,
                                      col.key,
                                      e.target.value || null
                                    )
                                  }
                                  style={listBuilderStyles.statusSelect}>
                                  <option value="">None</option>
                                  {col.options.map(opt => (
                                    <option key={opt.value} value={opt.value}>
                                      {opt.label}
                                    </option>
                                  ))}
                                </select>
                              </td>
                            );
                          })}
                          <td style={listBuilderStyles.tableCellActions}>
                            <LgmButton
                              variant="outlined"
                              onClick={() => handleRemoveItem(item.id)}
                              disabled={disabled}
                              style={listBuilderStyles.deleteButton}
                              hoverStyle={listBuilderStyles.deleteButtonHover}
                              pressedStyle={
                                listBuilderStyles.deleteButtonPressed
                              }
                              aria-label={`Remove ${item.label}`}>
                              <Trash2 size={16} />
                            </LgmButton>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div style={listBuilderStyles.noItemsText}>
                {config.noItemsText || 'No items added'}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Add Items Modal */}
      {isAddModalOpen && (
        <AddItemModal
          title={config.addModalTitle || 'Add Items'}
          availableItems={filteredAvailableItems}
          onConfirm={handleAddItems}
          onCancel={() => setIsAddModalOpen(false)}
        />
      )}
    </div>
  );
};

const listBuilderStyles: Record<string, React.CSSProperties> = {
  // No pointerEvents: 'none' here -- it used to also block the collapse
  // toggle in the header, which must stay clickable regardless of
  // `disabled` (view mode never mutates anything, just shows/hides rows).
  // Every actually-interactive control (Add/Delete buttons, status select)
  // already carries its own `disabled` prop.
  containerDisabled: {
    opacity: 0.6,
  },
  tableContainer: {
    border: `1px solid ${GRAY_200}`,
    borderRadius: '6px',
    overflow: 'hidden',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
  },
  tableHeaderRow: {
    backgroundColor: GRAY_50,
  },
  tableHeaderCell: {
    padding: '10px 12px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 600,
    color: GRAY_500,
    borderBottom: `1px solid ${GRAY_200}`,
  },
  tableHeaderCellActions: {
    padding: '10px 12px',
    textAlign: 'right',
    fontSize: '12px',
    fontWeight: 600,
    color: GRAY_500,
    borderBottom: `1px solid ${GRAY_200}`,
    width: '60px',
  },
  tableRow: {
    borderBottom: `1px solid ${GRAY_200}`,
  },
  tableCell: {
    padding: '10px 12px',
    fontSize: '14px',
    color: GRAY_700,
  },
  tableCellDisabled: {
    color: GRAY_400,
  },
  tableCellStatus: {
    padding: '10px 12px',
    width: '200px',
  },
  tableCellActions: {
    padding: '10px 12px',
    textAlign: 'right',
  },
  statusSelect: {
    width: '100%',
    padding: '6px 8px',
    fontSize: '14px',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    color: GRAY_700,
    cursor: 'pointer',
  },
  textInput: {
    width: '100%',
    padding: '6px 8px',
    fontSize: '14px',
    border: `1px solid ${GRAY_300}`,
    borderRadius: '4px',
    backgroundColor: '#ffffff',
    color: GRAY_700,
  },
  statusSelectDisabled: {
    backgroundColor: GRAY_50,
    color: GRAY_400,
    cursor: 'not-allowed',
  },
  deleteButton: {
    padding: '6px 8px',
    backgroundColor: 'transparent',
    border: `1px solid ${GRAY_200}`,
    borderRadius: '4px',
    color: '#dc2626',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  deleteButtonHover: {
    color: COLOR_DANGER_TEXT,
    backgroundColor: 'transparent',
    border: `1px solid ${GRAY_200}`,
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  deleteButtonPressed: {
    color: '#7f1d1d', // active: one step darker than hover
    backgroundColor: 'transparent',
    border: `1px solid ${GRAY_200}`,
    borderRadius: '4px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'all 0.2s',
  },
  noItemsText: {
    padding: '32px',
    textAlign: 'center',
    color: GRAY_400,
    fontSize: '14px',
    border: `1px dashed ${GRAY_300}`,
    borderRadius: '6px',
  },
};

export default React.memo(ListBuilder);
